import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../context/TranslationContext';
import {
  calcKeralitAreaSqm,
  calcKeralitDimensionsFromAreaSqm,
  KERALIT_MIN_AREA_SQM,
  KERALIT_MAX_AREA_SQM,
  KERALIT_MIN_DIMENSION_MM,
  KERALIT_MAX_DIMENSION_MM,
} from '../lib/pdfPricing';

const MIN_DIMENSION = KERALIT_MIN_DIMENSION_MM;
const MAX_DIMENSION = KERALIT_MAX_DIMENSION_MM;

const KeralitDrawingBoard = ({
  width = 1500,
  height = 1500,
  onDimensionsChange,
}) => {
  const { t } = useTranslation();
  const [areaSqmInput, setAreaSqmInput] = useState('');
  const areaSqmFocusedRef = useRef(false);

  const currentAreaSqm = calcKeralitAreaSqm(width, height);

  useEffect(() => {
    if (!areaSqmFocusedRef.current) {
      setAreaSqmInput(
        currentAreaSqm > 0 ? String(Number(currentAreaSqm.toFixed(2))) : ''
      );
    }
  }, [width, height, currentAreaSqm]);

  const formatAreaSqmDisplay = (sqm) =>
    sqm > 0 ? String(Number(sqm.toFixed(2))) : '';

  const commitAreaSqm = () => {
    if (!onDimensionsChange) return;

    const parsed = parseFloat(String(areaSqmInput).replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setAreaSqmInput(formatAreaSqmDisplay(currentAreaSqm));
      return;
    }

    const { width: newWidth, height: newHeight, valid } = calcKeralitDimensionsFromAreaSqm(
      parsed,
      width,
      height,
      { minDimension: MIN_DIMENSION, maxDimension: MAX_DIMENSION }
    );

    if (!valid) {
      setAreaSqmInput(formatAreaSqmDisplay(currentAreaSqm));
      return;
    }

    setAreaSqmInput(formatAreaSqmDisplay(calcKeralitAreaSqm(newWidth, newHeight)));
    onDimensionsChange(newWidth, newHeight);
  };

  return (
    <div className="flex h-full min-h-[500px] w-full items-center justify-center bg-[#F5F5F5] p-4 sm:p-8">
      <div className="flex w-full max-w-lg flex-col items-center rounded-xl border border-gray-200 bg-white px-8 py-10 shadow-sm sm:px-12 sm:py-12">
        <p className="mb-4 text-sm font-medium text-gray-600">{t('drawing.areaSqm')}</p>
        <div className="flex items-baseline justify-center gap-3 sm:gap-4">
          <input
            type="number"
            min={KERALIT_MIN_AREA_SQM}
            max={KERALIT_MAX_AREA_SQM}
            step="0.01"
            value={areaSqmInput}
            aria-label={t('drawing.areaSqm')}
            onChange={(e) => setAreaSqmInput(e.target.value)}
            onFocus={() => {
              areaSqmFocusedRef.current = true;
            }}
            onBlur={() => {
              areaSqmFocusedRef.current = false;
              commitAreaSqm();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            className="min-w-[10rem] w-[11rem] flex-shrink-0 border-0 border-b-2 border-orange-500 bg-transparent px-1 text-center text-5xl font-bold tabular-nums text-gray-900 outline-none focus:border-orange-600 sm:min-w-[11rem] sm:w-[13rem] sm:text-6xl [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          />
          <span className="flex-shrink-0 text-3xl font-semibold text-gray-500 sm:text-4xl">m²</span>
        </div>
        <p className="mt-6 text-center text-xs text-gray-500 sm:text-sm">
          {t('drawing.keralitAreaHint')}
        </p>
      </div>
    </div>
  );
};

export default KeralitDrawingBoard;
